/**
 * AdminNormalizeImagesCard — one-click "collapse legacy URLs" utility.
 *
 * Runs POST /api/admin/products/normalize-image-urls which idempotently:
 *   - strips any doubled `/api/api/uploads/` prefix on product image_url
 *     and every gallery_images entry
 *   - collapses absolute `https://<backend>/api/uploads/…` URLs down to
 *     the portable relative form so the same DB works in preview + prod.
 *
 * Safe to run repeatedly — reports fixed counts.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Wrench, CheckCircle2, Loader2 } from "lucide-react";

export default function AdminNormalizeImagesCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/products/normalize-image-urls`,
        {},
        { withCredentials: true, timeout: 120000 },
      );
      setResult(r.data);
      const total =
        (r.data?.products_with_primary_fixed || 0) +
        (r.data?.products_with_gallery_fixed || 0);
      toast.success(
        total > 0
          ? `Normalized ${total} product image URL${total === 1 ? "" : "s"}`
          : "All product image URLs are already clean",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to normalize URLs");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-tbc-navy/10 bg-white p-6"
      data-testid="admin-normalize-images-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-navy/60 font-semibold flex items-center gap-1.5">
            <Wrench className="w-3 h-3 text-tbc-gold" /> Legacy Image-URL Repair
          </p>
          <h3 className="font-heading text-lg text-tbc-navy mt-1 leading-tight">
            Normalize product image URLs
          </h3>
          <p className="text-sm text-tbc-navy/60 mt-1 max-w-xl">
            Fixes any product whose <code>image_url</code> or
            {" "}<code>gallery_images</code> was written before the ImageUpload
            path fix — strips the doubled <code>/api/api/</code> prefix and
            collapses absolute backend URLs to the portable relative form.
            Idempotent — safe to click any time.
          </p>
        </div>
        <Button
          onClick={run}
          disabled={busy}
          className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full"
          data-testid="normalize-images-run"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Normalizing…
            </>
          ) : (
            <>
              <Wrench className="w-4 h-4 mr-2" /> Normalize now
            </>
          )}
        </Button>
      </div>

      {result && (
        <div
          className="mt-4 rounded-lg bg-tbc-cream/60 border border-tbc-gold/30 p-3 flex items-center gap-3 text-sm text-tbc-navy"
          data-testid="normalize-images-result"
        >
          <CheckCircle2 className="w-4 h-4 text-tbc-gold flex-none" />
          <span>
            Primary image_url fixed: <strong>{result.products_with_primary_fixed}</strong>
            {" · "}
            Gallery entries fixed: <strong>{result.products_with_gallery_fixed}</strong>
          </span>
        </div>
      )}
    </div>
  );
}
